import 'zod/compile';
import { z } from 'zod';
import { useEffect } from 'react';
import { useGT } from 'gt-react';
import { SquaresFourIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { orpc } from 'src/api/rpc/client';
import { useAgentState } from '@/context/agent-state.context';
import { useAgentModule } from '@/hooks/useAgentModule';
import { useFrontendTool } from '@/hooks/useFrontendTool';
import type { AppController } from './$appId/appController';
import type { AppBehaviorHost } from './$appId/appBehaviorHost';
import { appLiveState } from './$appId/appLiveState';

/** Agent tools that save an App; any of them means the Apps queries are stale. */
const APP_SAVING_TOOLS: ReadonlySet<string> = new Set([
  'create_app',
  'deploy_app_version',
]);

function isFailedResult(result: unknown): boolean {
  return (
    typeof result === 'object' &&
    result !== null &&
    'ok' in result &&
    result.ok === false
  );
}

/**
 * Puts the Apps builder in the assistant panel. On an App's page, `appId`
 * tells the agent which App "this App" is. A saved App or version refreshes
 * the list and the open App, so the user sees the agent's work land.
 */
export function useAppsAgentModule(
  appId?: string,
  controller?: AppController,
  behaviors?: AppBehaviorHost | null,
) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const { registerToolResultListener } = useAgentState();

  useEffect(() => {
    return registerToolResultListener((_toolCallId, toolName, result) => {
      if (!APP_SAVING_TOOLS.has(toolName) || isFailedResult(result)) return;
      void queryClient.invalidateQueries({ queryKey: orpc.apps.key() });
    });
  }, [queryClient, registerToolResultListener]);

  const readLiveState = useFrontendTool({
    name: 'read_app_live_state',
    description:
      'Read the current browser App instance, deployment, unsaved bound inputs, selected row, command status, feed errors, behavior instances and their sessions, and a bounded causal trace of actions: spec binding, resolved input, operation, command invocation, outcome, accepted state paths and emitted facts. Call on demand to explain the current screen. Read-only; it runs no command and replays nothing. Local table sorting/filtering and other unbound widget state are not included.',
    parameters: z.object({
      recentEvents: z
        .number()
        .int()
        .min(1)
        .max(40)
        .default(20)
        .describe('Number of recent events to include, up to 40'),
    }),
    readOnlyHint: true,
    awaitable: true,
    enabled: controller !== undefined,
    execute: ({ recentEvents }) => {
      if (!controller || controller.inspect().disposed) {
        return { ok: false, error: 'This App instance is no longer open.' };
      }
      return appLiveState(controller, behaviors ?? null, recentEvents);
    },
  });

  useAgentModule({
    tools: [readLiveState],
    scope: controller
      ? `app:${controller.identity.appId}:${controller.identity.instanceId}`
      : appId
        ? `app:${appId}`
        : 'apps',

    moduleKey: 'apps',
    currentPage: appId ? `App ${appId}` : 'Apps',
    getModuleContext: () => (appId ? { appId } : undefined),
    placeholder: appId
      ? gt('Ask to change this App…')
      : gt('Describe the App you want to build…'),
    headerTitle: gt('Apps'),
    // The assistant is how Apps are built, so the panel is on regardless of
    // the chat flags (as on Workbooks).
    forcePanelEnabled: true,
    headerIcon: (
      <SquaresFourIcon size={18} weight="duotone" className="text-primary" />
    ),
  });
}
